// src/store/chatStore.ts

import { create } from "zustand";
import { ConversationData } from "../interfaces/all";
import { CacheManager } from "../utils/cache-manager";

const SELECTED_CONVERSATION_KEY = "selected_conversation";
const DEBOUNCE_DELAY = 300;

let updateTimeout: ReturnType<typeof setTimeout> | undefined;
let lastValidPhone: string | null = null;

const sanitizeConversationForStorage = (
  conversation: ConversationData,
): ConversationData => {
  const sanitized = { ...conversation };

  // Limpar objetos Date que não são serializáveis
  if (sanitized.last_message?.timestamp) {
    sanitized.last_message = {
      ...sanitized.last_message,
      timestamp: (sanitized.last_message.timestamp instanceof Date
        ? sanitized.last_message.timestamp.toISOString()
        : String(sanitized.last_message.timestamp)) as unknown as Date,
    };
  }

  const sanitizedConversation: ConversationData = {
    id: sanitized.id,
    name: sanitized.name,
    email: sanitized.email,
    telephone: sanitized.telephone,
    valid_name: sanitized.valid_name,
    valid_telephone: sanitized.valid_telephone,
    status: sanitized.status,
    campaign_id: sanitized.campaign_id,
    campaign_name: sanitized.campaign_name,
    company_id: sanitized.company_id,
    attendant_id: sanitized.attendant_id,
    attendant_name: sanitized.attendant_name,
    coord_id: sanitized.coord_id,
    coordinator_name: sanitized.coordinator_name,
    is_online: sanitized.is_online,
    last_message: sanitized.last_message
      ? {
          ...sanitized.last_message,
          timestamp: (sanitized.last_message.timestamp instanceof Date
            ? sanitized.last_message.timestamp.toISOString()
            : String(sanitized.last_message.timestamp)) as unknown as Date,
        }
      : undefined,
  };

  return sanitizedConversation;
};

const persistConversationToCache = (conversation: ConversationData | null) => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = undefined;
  }

  if (conversation) {
    const currentPhone = conversation.valid_telephone || conversation.telephone;

    if (lastValidPhone !== currentPhone) {
      lastValidPhone = currentPhone;
    }

    updateTimeout = setTimeout(() => {
      if (!conversation.id) {
        return;
      }

      const sanitizedConversation =
        sanitizeConversationForStorage(conversation);
      CacheManager.set(SELECTED_CONVERSATION_KEY, sanitizedConversation, {
        expirationDays: 7,
      });
    }, DEBOUNCE_DELAY);
  } else {
    CacheManager.remove(SELECTED_CONVERSATION_KEY);
    lastValidPhone = null;
  }
};

const getInitialConversation = (): ConversationData | null => {
  try {
    const cached = CacheManager.get<ConversationData>(
      SELECTED_CONVERSATION_KEY,
      {
        expirationDays: 7,
      },
    );

    if (cached) {
      if (!cached.id) {
        CacheManager.remove(SELECTED_CONVERSATION_KEY);
        return null;
      }
      return cached;
    }
  } catch (e) {
    console.error("Erro ao ler conversa selecionada do cache:", e);
  }

  return null;
};

interface ChatStore {
  selectedConversation: ConversationData | null;
  setSelectedConversation: (conversation: ConversationData | null) => void;
  clearSelectedConversation: () => void;
  typingState: Record<string, boolean>;
  setTyping: (phoneNumber: string, isTyping: boolean) => void;
}

const typingTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

export const useChatStore = create<ChatStore>((set) => ({
  selectedConversation: getInitialConversation(),
  setSelectedConversation: (conversation) => {
    set((state) => {
      if (state.selectedConversation?.id === conversation?.id) {
        return state;
      }
      persistConversationToCache(conversation);
      return { selectedConversation: conversation };
    });
  },
  clearSelectedConversation: () => {
    set((state) => {
      if (state.selectedConversation === null) {
        return state;
      }
      persistConversationToCache(null);
      return { selectedConversation: null };
    });
  },
  typingState: {},
  setTyping: (phoneNumber, isTyping) => {
    // Clear any existing timeout for this user
    if (typingTimeouts[phoneNumber]) {
      clearTimeout(typingTimeouts[phoneNumber]);
      delete typingTimeouts[phoneNumber];
    }

    if (isTyping) {
      // Set a safety timeout of 4 seconds to clear the typing status
      typingTimeouts[phoneNumber] = setTimeout(() => {
        set((state) => {
          const newTypingState = { ...state.typingState };
          delete newTypingState[phoneNumber];
          return { typingState: newTypingState };
        });
        delete typingTimeouts[phoneNumber];
      }, 4000);
    }

    set((state) => {
      const currentVal = !!state.typingState[phoneNumber];
      if (currentVal === isTyping) {
        return state;
      }
      const newTypingState = { ...state.typingState };
      if (isTyping) {
        newTypingState[phoneNumber] = true;
      } else {
        delete newTypingState[phoneNumber];
      }
      return { typingState: newTypingState };
    });
  },
}));
